import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
  name: "referenceData",
  initialState: {
    departments: [],
    workflowPerformers: [],
    partnerPerformers: [],
    codeManagers: {},
    requests: {
      departments: createRequestState(),
      workflowPerformers: createRequestState(),
      partnerPerformers: createRequestState(),
    },
  },
  reducers: {
    referenceRequestStarted: (referenceData, action) => {
      requestStarted(referenceData.requests, action.payload);
    },
    referenceRequestFailed: (referenceData, action) => {
      requestFailed(referenceData.requests, action.payload?.key, action.payload?.error || action.payload);
    },
    departmentsReceived: (referenceData, action) => {
      referenceData.departments = action.payload;
      requestSucceeded(referenceData.requests, "departments");
    },
    workflowPerformersReceived: (referenceData, action) => {
      referenceData.workflowPerformers = action.payload;
      requestSucceeded(referenceData.requests, "workflowPerformers");
    },
    partnerPerformersReceived: (referenceData, action) => {
      referenceData.partnerPerformers = action.payload;
      requestSucceeded(referenceData.requests, "partnerPerformers");
    },
    codeManagerReceived: (referenceData, action) => {
      const { typeName, data } = action.payload;
      referenceData.codeManagers[typeName] = data;
      requestSucceeded(referenceData.requests, `codeManager:${typeName}`);
    },
  },
});

export const {
  referenceRequestStarted,
  referenceRequestFailed,
  departmentsReceived,
  workflowPerformersReceived,
  partnerPerformersReceived,
  codeManagerReceived,
} = slice.actions;

export default slice.reducer;

export const loadDepartments = (forceRefresh = false) =>
  apiCallBegan({
    url: config.GetAllDepartments,
    onStart: { type: referenceRequestStarted.type, payload: "departments" },
    onSuccess: departmentsReceived.type,
    onError: { type: referenceRequestFailed.type, payload: { key: "departments" } },
    cache: true,
    cacheDuration: 300000,
    forceRefresh,
  });

export const loadWorkflowPerformers = (forceRefresh = false) =>
  apiCallBegan({
    url: config.GetAllWorkflowPerformers,
    onStart: { type: referenceRequestStarted.type, payload: "workflowPerformers" },
    onSuccess: workflowPerformersReceived.type,
    onError: { type: referenceRequestFailed.type, payload: { key: "workflowPerformers" } },
    cache: true,
    cacheDuration: 300000,
    forceRefresh,
  });

export const loadPartnerPerformers = (forceRefresh = false) =>
  apiCallBegan({
    url: config.GetAllPartnerPerformers,
    onStart: { type: referenceRequestStarted.type, payload: "partnerPerformers" },
    onSuccess: partnerPerformersReceived.type,
    onError: { type: referenceRequestFailed.type, payload: { key: "partnerPerformers" } },
    cache: true,
    cacheDuration: 300000,
    forceRefresh,
  });

export const loadCodeManager = (typeName, forceRefresh = false) =>
  apiCallBegan({
    url: `${config.GetCodemanager}${typeName}`,
    onStart: { type: referenceRequestStarted.type, payload: `codeManager:${typeName}` },
    onSuccess: { type: codeManagerReceived.type, payload: { typeName } },
    onError: { type: referenceRequestFailed.type, payload: { key: `codeManager:${typeName}` } },
    cache: true,
    cacheDuration: 300000,
    cacheKey: `codemanager:${typeName}`,
    forceRefresh,
  });

export const getDepartments = createSelector(
  (state) => state.entities.referenceData,
  (referenceData) => referenceData.departments,
);

export const getWorkflowPerformers = createSelector(
  (state) => state.entities.referenceData,
  (referenceData) => referenceData.workflowPerformers,
);

export const getPartnerPerformers = createSelector(
  (state) => state.entities.referenceData,
  (referenceData) => referenceData.partnerPerformers,
);

export const getCodeManager = (typeName) =>
  createSelector(
    (state) => state.entities.referenceData,
    (referenceData) => referenceData.codeManagers[typeName] || [],
  );

export const getReferenceRequests = createSelector(
  (state) => state.entities.referenceData,
  (referenceData) => referenceData.requests,
);
